import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Input,
  Modal,
  ModalFooter,
  ModalBody,
  ModalHeader
} from "reactstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import classnames from "classnames";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import TableContainer from "../../Components/Common/TableContainer";
import DeleteModal from "../../Components/Common/DeleteModal";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import {
  assignBulkDriverToOrders,
  listOrder,
  OrderStatus,
  updateBulkSubscriptionStatusUpdate,
  updateOrdersRouteStoreDriver,
  UpdateOrderStatus
} from "./_requests";
import { Pencil } from "react-bootstrap-icons";
import OrderStatusModal from "./OrderStatusModal";
import { useToast } from "../../Components/Common/ToastContext";
import { listDriver, listStore } from "../../pages/Route/_requests";
import { listRoute } from "../../pages/Route/_requests";
import { listDeliveryInstructions } from "./_requests";
import OrderRouteUpdate from "./OrderRouteUpdate";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from "xlsx";
import ExportCSVModal from "../../Components/Common/ExportCSVModal";
import jsPDF from "jspdf";
import "jspdf-autotable";
import OrderStoreUpdate from "./OrderStoreUpdate";
import OrderDeliveryDateUpdate from "./OrderDeliveryDateUpdate";
import { formatDate } from "../../helpers/FunctionHelpers";
import { getSocket } from "../../helpers/socket";

// Define interface for BulkStatusModal props
interface BulkStatusModalProps {
  isOpen: boolean;
  toggle: () => void;
  selectedStatus: string;
  setSelectedStatus: (status: string) => void;
  orderStatusLists: { id: number; name: string }[];
  handleBulkStatusUpdate: () => void;
}

// Define interface for BulkDriverModal props
interface BulkDriverModalProps {
  isOpen: boolean;
  toggle: () => void;
  selectedDriver: string;
  setSelectedDriver: (driver: string) => void;
  driversList: any[];
  handleBulkDriverAssign: () => void;
  selectedOrdersCount: number;
}

// Create BulkStatusModal as a separate component
const BulkStatusModal: React.FC<BulkStatusModalProps> = ({
  isOpen,
  toggle,
  selectedStatus,
  setSelectedStatus,
  orderStatusLists,
  handleBulkStatusUpdate
}) => (
  <Modal isOpen={isOpen} toggle={toggle}>
    <ModalHeader toggle={toggle}>Bulk Status Update</ModalHeader>
    <ModalBody>
      <div className="mb-3">
        <label className="form-label">Select Status</label>
        <select
          className="form-control"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option value="">Select Status</option>
          {orderStatusLists?.map((status) => (
            <option key={status.id} value={status.id}>
              {status.name}
            </option>
          ))}
        </select>
      </div>
    </ModalBody>
    <ModalFooter>
      <button type="button" className="btn btn-light" onClick={toggle}>
        Cancel
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={handleBulkStatusUpdate}
        disabled={!selectedStatus}
      >
        Update Status
      </button>
    </ModalFooter>
  </Modal>
);

// Create BulkDriverModal as a separate component
const BulkDriverModal: React.FC<BulkDriverModalProps> = ({
  isOpen,
  toggle,
  selectedDriver,
  setSelectedDriver,
  driversList,
  handleBulkDriverAssign,
  selectedOrdersCount
}) => (
  <Modal isOpen={isOpen} toggle={toggle}>
    <ModalHeader toggle={toggle}>Bulk Driver Assignment</ModalHeader>
    <ModalBody>
      <div className="mb-3">
        <label className="form-label">Select Driver</label>
        <select
          className="form-control"
          value={selectedDriver}
          onChange={(e) => setSelectedDriver(e.target.value)}
        >
          <option value="">Select Driver</option>
          {driversList?.map((driver) => (
            <option key={driver.id} value={driver.id}>
              {driver.name} - {driver.contact_number}
            </option>
          ))}
        </select>
        <div className="mt-2 text-muted">
          This will assign the selected driver to {selectedOrdersCount} order(s)
        </div>
      </div>
    </ModalBody>
    <ModalFooter>
      <button type="button" className="btn btn-light" onClick={toggle}>
        Cancel
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={handleBulkDriverAssign}
        disabled={!selectedDriver}
      >
        Assign Driver
      </button>
    </ModalFooter>
  </Modal>
);

const Orders = () => {
  const navigate = useNavigate();
  const [modal, setModal] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState("all");
  const [orderList, setOrderList] = useState<any>([]);
  const [orderFilterList, setOrderFilterList] = useState<any>([]);
  const [order, setOrder] = useState<any>([]);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState<boolean>(false);
  const [orderStatusLists, setStatus] = useState<{ id: number; name: string }[]>([]);
  const [orderCounts, setOrderCounts] = useState<{ [key: string]: number }>({});
  const [totalOrders, setTotalOrders] = useState(0);
  const [deliveryInstructions, setDeliveryInstructions] = useState<any[]>([]);
  const { showToast, toaster, clearToast } = useToast();
  const [isRouteModalOpen, setIsRouteModalOpen] = useState(false);
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [isDeliveryDateModalOpen, setIsDeliveryDateModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedRoute, setSelectedRoute] = useState("");
  const [selectedStore, setSelectedStore] = useState("");
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState<Date | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [ordersListRoute, setRoute] = useState<any>([]);
  const [ordersListStore, setStore] = useState<any>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>("");
  const [selectedRouteFilter, setSelectedRouteFilter] = useState<string>("");
  const [selectedSlotFilter, setSelectedSlotFilter] = useState<string>("");
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<number>(0); // Default to All
  const [selectedUserFilter, setSelectedUserFilter] = useState<string>("");
  const [selectedDriverFilter, setSelectedDriverFilter] = useState<string>(""); // New state for driver filter
  const [userList, setUserList] = useState<any>([]); // State to store user list
  const [selectedOrders, setSelectedOrders] = useState<number[]>([]); // State for selected order IDs
  const [bulkStatusModal, setBulkStatusModal] = useState(false);
  const [selectedBulkStatus, setSelectedBulkStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // New states for bulk driver assignment
  const [bulkDriverModal, setBulkDriverModal] = useState(false);
  const [selectedBulkDriver, setSelectedBulkDriver] = useState("");
  const [driversList, setDriversList] = useState<any[]>([]);
  const [currentDateFilter, setCurrentDateFilter] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.state) {
      const { selectedDate, type } = location.state;
      if (selectedDate) {
        const date = new Date(selectedDate);
        setStartDate(date);
        setEndDate(date);
      }
      if (type !== undefined) {
        setSelectedTypeFilter(Number(type));
      }
    }
  }, [location.state]);

  // Function to toggle order selection for ALL order types
  const toggleOrderSelection = useCallback((orderId: number) => {
    setSelectedOrders(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  }, []);

  // Function to select all orders (ALL types)
  const selectAllOrders = useCallback(() => {
    const orderIds = orderList?.map((order: any) => order.id);
    setSelectedOrders(orderIds);
  }, [orderList]);

  // Function to clear all selections
  const clearAllSelections = useCallback(() => {
    setSelectedOrders([]);
  }, []);

  // Check if all orders are selected
  const areAllOrdersSelected = useMemo(() => {
    return orderList?.length > 0 && selectedOrders.length === orderList?.length;
  }, [orderList, selectedOrders]);

  // Check if any orders are selected
  const areAnyOrdersSelected = selectedOrders.length > 0;

  // Filter orders for driver assignment (selected orders with routes)
  const filteredOrdersForDriver = useMemo(() => {
    return orderList?.filter((order: any) =>
      selectedOrders.includes(order.id) &&
      order.route?.id
    );
  }, [orderList, selectedOrders]);

  // Bulk status update function
  const handleBulkStatusUpdate = async (statusId?: number) => {
    if (selectedOrders.length == 0) {
      showToast("Please select at least one order to update", false);
      return;
    }

    try {
      // Use the provided statusId or the selectedBulkStatus
      const statusToUpdate = statusId || parseInt(selectedBulkStatus);

      // Send array of order IDs instead of single order_id
      const data = {
        order_ids: selectedOrders, // Changed from order_id to order_ids (array)
        order_status_id: statusToUpdate
      };

      console.log("Updating orders:", selectedOrders, "to status:", statusToUpdate);

      const res: any = await updateBulkSubscriptionStatusUpdate(data);

      if (res.status === true) {
        showToast(res?.message || `Successfully updated ${selectedOrders.length} order(s) to status ${statusToUpdate}`, true);
        setSelectedOrders([]);
        setBulkStatusModal(false);

        // Refresh the order list
        listFetchOrder();
      } else {
        showToast(res.message || 'Failed to update order statuses', false);
      }
    } catch (error) {
      console.error('Error updating bulk order status:', error);
      showToast('Failed to update order statuses', false);
    }
  };

  // Bulk driver assignment function
  const handleBulkDriverAssign = async () => {
    if (selectedOrders.length === 0) {
      showToast("Please select at least one order to assign driver", false);
      return;
    }

    if (filteredOrdersForDriver.length === 0) {
      showToast("Selected orders must have routes assigned first", false);
      return;
    }

    try {
      const driverId = parseInt(selectedBulkDriver);

      // Group orders by route and delivery date
      const ordersByRouteAndDate: { [key: string]: any } = {};

      filteredOrdersForDriver.forEach((order: any) => {
        const routeId = order.route?.id;
        const deliveryDate = order.delivery_date;
        const key = `${routeId}-${deliveryDate}`;

        if (!ordersByRouteAndDate[key]) {
          ordersByRouteAndDate[key] = {
            driver_id: driverId,
            order_ids: [],
            route_id: routeId,
            delivery_date: deliveryDate,
            updatedBy: 1 // You might want to get this from user context
          };
        }

        ordersByRouteAndDate[key].order_ids.push(order.id);
      });

      // Convert to array format as required
      const assignmentData = Object.values(ordersByRouteAndDate);

      console.log("Assigning driver to orders:", assignmentData);

      const res: any = await assignBulkDriverToOrders(assignmentData);

      if (res.status === true) {
        showToast(res?.message || `Successfully assigned driver to ${filteredOrdersForDriver.length} order(s)`, true);
        setBulkDriverModal(false);
        setSelectedBulkDriver("");
        setSelectedOrders([]);

        // Refresh the order list
        listFetchOrder();
      } else {
        showToast(res.message || 'Failed to assign driver to orders', false);
      }
    } catch (error) {
      console.error('Error assigning bulk driver:', error);
      showToast('Failed to assign driver to orders', false);
    }
  };

  const toggleModal = useCallback((orderId?: number, event?: React.MouseEvent) => {
    event?.stopPropagation();

    if (orderId !== undefined) {
      setSelectedOrderId(orderId);
      const order = orderList?.find((o: any) => o.id == orderId);
      setSelectedStatus(order?.orderStatus?.id.toString() || "");
    }

    setIsModalOpen(prev => {
      if (orderId === undefined) return false;
      return !prev;
    });
  }, [orderList]);

  const toggleRouteModal = (orderId?: number) => {
    if (orderId !== undefined) {
      setSelectedOrderId(orderId);
      const order = orderList?.find((o: any) => o.id == orderId);
      setSelectedRoute(order?.route?.id.toString() || "");
    }
    setIsRouteModalOpen(prev => !prev);
  };

  const toggleStoreModal = (orderId?: number) => {
    if (orderId !== undefined) {
      setSelectedOrderId(orderId);
      const order = orderList?.find((o: any) => o.id === orderId);
      setSelectedStore(order?.store?.id.toString() || "");
    }
    setIsStoreModalOpen(prev => !prev);
  };
  console.log("orderFilterList------------------", orderFilterList);
  const toggleDeliveryDateModal = (orderId?: number) => {
    if (!orderList || orderList?.length === 0) return;
    if (orderId != undefined) {
      setSelectedOrderId(orderId);
      const selectedOrder = orderFilterList?.find((order: any) => order.id == orderId);
      if (selectedOrder && selectedOrder.delivery_date) {
        setSelectedDeliveryDate(new Date(selectedOrder.delivery_date));
      } else {
        setSelectedDeliveryDate(null);
      }
    }
    setIsDeliveryDateModalOpen((prev) => !prev);
  };

  useEffect(() => {
    if (toaster?.message) {
      if (toaster.status === true) {
        toast.success(toaster.message, { autoClose: 3000 });
      } else {
        toast.error(toaster.message, { autoClose: 3000 });
      }
      clearToast();
    }
  }, [toaster, clearToast]);

  const listFetchOrder = async () => {
    setIsLoading(true);
    try {
      const res: any = await listOrder();
      if (!res || !res.data) return;

      let orders = res.data;
      if (Array.isArray(orders)) {
        orders = Array.from(new Map(orders.map((item: any) => [item.id, item])).values());
      }
      setTotalOrders(orders.length);

      const statusCounts: { [key: string]: number } = {};
      orders.forEach((order: any) => {
        const status = order.orderStatus?.name || "Unknown";
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      });

      setOrderCounts(statusCounts);
      setOrderList(Array.isArray(orders) ? orders : []);
      setOrderFilterList(Array.isArray(orders) ? orders : []);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteOrder = () => {
    if (order) {
      setDeleteModal(false);
    }
  };

  // Fetch drivers list
  const fetchDriversList = async () => {
    try {
      const res = await listDriver();
      console.log("res.data listDriver", res.data)
      setDriversList(res.data || []);
    } catch (error) {
      console.error("Error fetching drivers:", error);
    }
  };

  useEffect(() => {
    listFetchOrder();
    OrderStatus().then((res: any) => {
      setStatus(res.data);
    });

    listStore().then((res: any) => {
      setStore(res.data);
    });

    listRoute().then((res: any) => {
      setRoute(res.data);
    });

    listOrder().then((res: any) => {
      const users = res?.data?.map((order: any) => order.user?.name).filter((name: any) => name);
      setUserList(Array.from(new Set(users)));
    });

    fetchDriversList();
  }, []);





  const toggleTab = (statusId: string | "all") => {
    setActiveTab(statusId);
  };

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setOrder(null);
    } else {
      setModal(true);
    }
  }, [modal]);

  const handleOrderClick = useCallback(
    (arg: any) => {
      const order = arg;
      setOrder({
        id: order.id,
        orderId: order.orderId,
        customer: order.customer,
        product: order.product,
        orderDate: order.orderDate,
        ordertime: order.ordertime,
        amount: order.amount,
        payment: order.payment,
        status: order.status,
      });

      setIsEdit(true);
      toggle();
    },
    [toggle]
  );



  const handleStatus = async () => {
    const data = { order_id: selectedOrderId, tracking_status: parseInt(selectedStatus) };
    const res: any = await UpdateOrderStatus(data);
    if (res.status === true) {
      showToast(res.message, res.status);
      listFetchOrder();
    } else {
      showToast(res.message, res.status);
    }
    toggleModal();
  };

  const handleRouteUpdate = async () => {
    const data = { order_id: selectedOrderId, route_id: selectedRoute };
    const res: any = await updateOrdersRouteStoreDriver(data);
    if (res.status === true) {
      showToast(res.message, res.status);
      listFetchOrder();
    } else {
      showToast(res.message, res.status);
    }
    toggleRouteModal();
  };

  const handleStoreUpdate = async () => {
    const data = { order_id: selectedOrderId, store_id: selectedStore };
    const res: any = await updateOrdersRouteStoreDriver(data);
    if (res.status === true) {
      showToast(res.message, res.status);
      listFetchOrder();
    } else {
      showToast(res.message, res.status);
    }
    toggleStoreModal();
  };

  const handleDeliveryDateUpdate = async () => {
    const data = { order_id: selectedOrderId, delivery_date: selectedDeliveryDate };
    const res: any = await updateOrdersRouteStoreDriver(data);
    if (res.status === true) {
      showToast(res.message, res.status);
      listFetchOrder();
    } else {
      showToast(res.message, res.status);
    }
    toggleDeliveryDateModal();
  };

  const applyFilters = useCallback(() => {
    let filteredOrders = [...orderFilterList];

    // Filter by Tab (Status)
    if (activeTab !== "all") {
      filteredOrders = filteredOrders?.filter(
        (order: any) => order.orderStatus?.id?.toString() === activeTab
      );
    }

    // Filter by Type
    if (selectedTypeFilter !== 0) {
      filteredOrders = filteredOrders?.filter(
        (order: any) => Number(order.type) === Number(selectedTypeFilter)
      );
    }

    // Filter by User Name
    if (selectedUserFilter) {
      filteredOrders = filteredOrders?.filter(
        (order: any) => order.user?.name?.toLowerCase().includes(selectedUserFilter.toLowerCase())
      );
    }

    // Filter by Date Range using moment for reliable day-level comparison
    if (startDate || endDate) {
      filteredOrders = filteredOrders?.filter((order: any) => {
        if (!order.delivery_date) return false;
        const deliveryDate = moment(order.delivery_date).startOf('day');

        const isAfterStart = !startDate || deliveryDate.isSameOrAfter(moment(startDate).startOf('day'));
        const isBeforeEnd = !endDate || deliveryDate.isSameOrBefore(moment(endDate).endOf('day'));

        return isAfterStart && isBeforeEnd;
      });
    }

    // Filter by Status Dropdown (if distinct from Tabs)
    if (selectedStatusFilter) {
      filteredOrders = filteredOrders?.filter(
        (order: any) => order.orderStatus?.name === selectedStatusFilter
      );
    }

    // Filter by Route
    if (selectedRouteFilter) {
      filteredOrders = filteredOrders?.filter(
        (order: any) => order.route?.name === selectedRouteFilter
      );
    }

    // Filter by Slot
    if (selectedSlotFilter) {
      filteredOrders = filteredOrders?.filter(
        (order: any) => order.slot === selectedSlotFilter
      );
    }

    // Filter by Driver
    if (selectedDriverFilter) {
      filteredOrders = filteredOrders?.filter(
        (order: any) => order.driver?.id?.toString() === selectedDriverFilter
      );
    }

    setOrderList(filteredOrders);
  }, [orderFilterList, selectedTypeFilter, selectedUserFilter, startDate, endDate, selectedStatusFilter, selectedRouteFilter, selectedSlotFilter, selectedDriverFilter, activeTab, orderStatusLists]);

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedStatusFilter("");
    setSelectedRouteFilter("");
    setSelectedSlotFilter("");
    setSelectedTypeFilter(0);
    setSelectedUserFilter("");
    setSelectedDriverFilter(""); // Clear driver filter
    setActiveTab("all");
    setOrderList([...orderFilterList]);
  };

  useEffect(() => {
    applyFilters();
  }, [startDate, endDate, selectedStatusFilter, selectedRouteFilter, selectedSlotFilter, selectedTypeFilter, selectedDriverFilter, selectedUserFilter, activeTab, applyFilters]);

  const [isExportCSV, setIsExportCSV] = useState<boolean>(false);

  // Download Excel function
  const downloadExcel = () => {
    // Prepare data for Excel
    const dataToExport = Array.isArray(orderList) ? orderList : [];
    const excelData = dataToExport.map((order: any) => ({
      "Order ID": order.order_code_id,
      "Order Date": order.order_date ? formatDate(order.order_date) : "",
      "Type": order.order_type_name || (order.type === 1 ? "Order" : "Subscription"),
      "Delivery Date": order.delivery_date ? formatDate(order.delivery_date) : "",
      "Slot": order.slot_type_name || "",
      "User Name": order.user?.name || "",
      "Phone No": order.user?.mobile_number || "",
      "Alt Phone No": order.alternate_contact_number || "",
      "Address": order.shipping_address || "",
      "Items": (order.orderItems || [])
        .sort((a: any, b: any) => {
          const aPop = a.product?.is_popular ? 1 : 0;
          const bPop = b.product?.is_popular ? 1 : 0;
          return bPop - aPop;
        })
        .map((item: any) =>
          `${item.product?.name || "Unknown"} (${item.variant_value}ml x ${item.quantity})`
        ).join("; ") || "",
      "Total Amount": `₹${order.total_amount?.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      "Route": order.route?.name || "",
      "Store": order.store?.name || "",
      "Driver": order.driver?.name || "",
      "Status": order.orderStatus?.name || "",
    }));

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

    // Generate Excel file
    XLSX.writeFile(workbook, `Orders_${moment().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`);

    showToast("Excel file downloaded successfully", true);
  };

  const columns = useMemo(() => [
    {
      header: (
        isLoading ? (
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        ) : (
          <Input
            type="checkbox"
            id="selectAllOrders"
            checked={areAllOrdersSelected}
            onChange={() => areAllOrdersSelected ? clearAllSelections() : selectAllOrders()}
            disabled={!orderList || orderList.length === 0}
          />
        )
      ),
      id: "selection",
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }: { row: { original: any } }) => {
        const order = row.original;
        return (
          <Input
            type="checkbox"
            checked={selectedOrders.includes(order.id)}
            onChange={() => toggleOrderSelection(order.id)}
          />
        );
      },
    },
    {
      header: "S.No",
      id: "serial",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell: any) => {
        return (
          <span>
            {cell.table.getSortedRowModel().flatRows.indexOf(cell.row) + 1}
          </span>
        );
      },
    },
    {
      header: "Order Id",
      accessorKey: "order_code_id",
      enableColumnFilter: false,
      cell: (cell: any) => {
        const row = cell.row.original;
        const date = row.order_date;
        const type = row.type;

        const typeName = row.order_type_name || (type === 1 ? "Order" : type === 2 ? "Subscription" : "");
        let label = typeName;
        let labelClass = "";

        if (typeName === "Order" || typeName === "Regular Order") {
          label = "Order";
          labelClass = "badge bg-primary-subtle text-primary ms-2";
        } else if (typeName === "Subscription" || typeName === "Subscription Order") {
          label = "Subscription";
          labelClass = "badge bg-success-subtle text-success ms-2";
        } else if (typeName === "Mixed Order") {
          label = "Mixed";
          labelClass = "badge bg-info-subtle text-info ms-2";
        } else if (typeName === "Trial Order") {
          label = "Trial";
          labelClass = "badge bg-warning-subtle text-warning ms-2";
        }

        const isFirstOrder = row.is_first_order || row.user?.is_trial_used === 0 || row.user?.is_trial_used === false;

        const matchedInstructions = (row.delivery_instructions_id || "")
          .split(",")
          .map((instId: string) => parseInt(instId.trim()))
          .filter(Boolean)
          .map((instId: number) => deliveryInstructions.find((item: any) => item.id === instId))
          .filter(Boolean);

        return (
          <>
            <Link to={`/orders/view/${row.id}`} className="custom-redirect-link">
              {cell.getValue()}
              {label && <span className={labelClass}>{label}</span>}
              {isFirstOrder && <span className="badge bg-danger text-white ms-1">NEW</span>}
            </Link>
            <div>
              {formatDate(date)}
            </div>
            {matchedInstructions.length > 0 && (
              <div className="mt-1 d-flex gap-1 flex-wrap">
                {matchedInstructions.map((inst: any, idx: number) => (
                  <span
                    key={idx}
                    className="badge"
                    style={{ backgroundColor: inst.color || '#6c757d' }}
                    title={inst.description}
                  >
                    {inst.name}
                  </span>
                ))}
              </div>
            )}
          </>
        );
      },
    },
    {
      header: "Delivery Date",
      accessorKey: "delivery_date",
      enableColumnFilter: false,
      cell: (cell: any) => {
        const date = cell.row.original.delivery_date;
        const slot_type_name = cell.row.original.slot_type_name;
        const orderId = cell.row.original.id;

        return (
          <>
            <div>
              <strong><small className="text-muted me-3">  {formatDate(date)}</small></strong>
              <br />
              ({slot_type_name})
            </div>
          </>
        );
      },
    },
    {
      header: "Items",
      accessorKey: "orderItems",
      enableColumnFilter: false,
      cell: ({ row }: { row: { original: any } }) => {
        const items = row.original.orderItems;

        return (
          <div className="d-flex flex-column gap-1">
            {items && items.length > 0 ? (
              [...items]
                .sort((a: any, b: any) => {
                  const aPop = a.product?.is_popular ? 1 : 0;
                  const bPop = b.product?.is_popular ? 1 : 0;
                  return bPop - aPop;
                })
                .map((item: any, index: number) => (
                  <div key={index} className="border p-1 rounded bg-light-subtle d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{item.product?.name || "Unknown Product"}</strong>
                      {item.product?.is_popular ? (
                        <span className="badge bg-danger-subtle text-danger ms-1" style={{ fontSize: '9px' }}>
                          <i className="ri-star-fill"></i> Popular
                        </span>
                      ) : null}
                      <span className={`badge ms-1 ${item.is_subscription ? 'bg-success-subtle text-success' : 'bg-primary-subtle text-primary'}`} style={{ fontSize: '9px' }}>
                        {item.is_subscription ? 'Subscription' : 'Order'}
                      </span>
                      {item.validity_info && (
                        <span className="badge bg-warning-subtle text-warning ms-1" style={{ fontSize: '9px' }}>
                          <i className="ri-calendar-event-line"></i> {item.validity_info}
                        </span>
                      )}
                    </div>
                    <small className="ms-2">
                      {item.variant_value}ml × {item.quantity} = ₹
                      {item.total_amount.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </small>
                  </div>
                ))
            ) : (
              <span className="text-muted">No items</span>
            )}
          </div>
        );
      },
    },
    {
      header: "Address",
      accessorKey: "shipping_address",
      enableColumnFilter: false,
      cell: ({ row }: { row: { original: any } }) => {
        const address = row.original.shipping_address || "_";
        const userName = row.original.user?.name || "_";
        const mobileNumber = row.original.user?.mobile_number || "_";
        const altMobile = row.original.alternate_contact_number;
        const userId = row.original.user?.id; // assuming user id is here

        return (
          <Link
            to={`/user/view/${userId}`}
            className="custom-redirect-link"
          >
            <div>
              <strong>{userName}</strong> - ({mobileNumber})
              {altMobile && (
                <div>
                  <small className="text-primary fw-semibold">Alt: {altMobile}</small>
                </div>
              )}
              <br />
              <span className="text-muted">{address}</span>
            </div>
          </Link>
        );
      },
    },
    {
      header: "Alt Phone",
      accessorKey: "alternate_contact_number",
      enableColumnFilter: false,
      cell: ({ row }: { row: { original: any } }) => {
        const altMobile = row.original.alternate_contact_number;
        return altMobile ? (
          <span className="fw-bold text-primary">{altMobile}</span>
        ) : (
          <span className="text-muted">-</span>
        );
      },
    },

    {
      header: "Amount",
      accessorKey: "total_amount",
      enableColumnFilter: false,
      cell: (cell: any) => {
        const amount = cell.getValue();
        return `₹${amount.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      },
    },
    {
      header: "Route",
      accessorKey: "route.name",
      enableColumnFilter: false,
      cell: ({ row }: { row: { original: any } }) => {
        const routeName = row.original.route?.name || "_";
        const orderId = row.original.id;

        return (
          <div className="d-flex align-items-center gap-2">
            <span className="fs-7 cursor-pointer" onClick={() => toggleRouteModal(orderId)}>
              {routeName}
            </span>
          </div>
        );
      },
    },
    {
      header: "Driver",
      accessorKey: "driver.name",
      enableColumnFilter: false,
      cell: ({ row }: { row: { original: any } }) => {
        const driver = row.original.driver;
        const orderId = row.original.id;
        return (
          <div className="d-flex align-items-center gap-2">
            <span className="fs-7 cursor-pointer">
              <strong>{driver?.name || "_"}</strong>
              <br />
              <span className="text-muted">{driver?.contact_number || "_"}</span>
            </span>
          </div>
        );
      },
    },
    {
      header: "Store",
      accessorKey: "store.name",
      enableColumnFilter: false,
      cell: ({ row }: { row: { original: any } }) => {
        const store = row.original.store;
        const orderId = row.original.id;
        return (
          <div className="d-flex align-items-center gap-2">
            <span className="fs-7 cursor-pointer" onClick={() => toggleStoreModal(orderId)}>
              <strong>{store?.name || "_"}</strong>
              <br />
              <span className="text-muted">{store?.address || "_"}</span>
            </span>
          </div>
        );
      },
    },
    {
      header: "Status",
      accessorKey: "orderStatus.id",
      enableColumnFilter: false,
      cell: ({ row }: { row: { original: any } }) => {
        const { id: statusId, name: statusName } = row.original.orderStatus || {};
        const orderId = row.original.id;

        const statusMap: Record<number, { text: string; class: string }> = {
          1: { text: statusName, class: "bg-primary-subtle text-primary" }, // Placed / New
          2: { text: statusName, class: "bg-info-subtle text-info" }, // Confirmed / Packed
          3: { text: statusName, class: "bg-danger-subtle text-danger" }, // Cancelled by Admin (Red)
          4: { text: statusName, class: "bg-warning-subtle text-warning" }, // Cancelled by User (Orange/Yellow)
          5: { text: statusName, class: "bg-info-subtle text-info" }, // Shipped
          6: { text: statusName, class: "bg-info-subtle text-info" }, // Out for Delivery
          7: { text: statusName, class: "bg-success-subtle text-success" }, // Delivered (Green)
          8: { text: statusName, class: "bg-secondary-subtle text-secondary" }, // Returned
          9: { text: statusName, class: "bg-dark-subtle text-dark" }, // Payment Failed
          10: { text: statusName, class: "bg-danger-subtle text-danger" }, // Unable to Deliver
          11: { text: statusName, class: "bg-dark-subtle text-dark" }, // Timeout
          12: { text: statusName, class: "bg-info-subtle text-info" }, // Refunded
        };

        const status = statusMap[statusId] || {
          text: statusName || "Nill",
          class: "bg-secondary-subtle text-secondary", // Default gray instead of yellow
        };

        return (
          <div className="d-flex align-items-center gap-2">
            <span
              className={`badge fw-bold fs-7 ${status.class}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleModal(orderId, e);
              }}
            >
              {status.text}
            </span>
            <Pencil
              size={14}
              className="text-muted cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                toggleModal(orderId, e);
              }}
            />
          </div>
        );
      },
    },
  ], [
    handleOrderClick,
    selectedOrders,
    areAllOrdersSelected,
    deliveryInstructions,
    isLoading,
    orderList,
    selectAllOrders,
    clearAllSelections,
    toggleOrderSelection,
    toggleModal,
    toggleRouteModal,
    toggleStoreModal
  ]);

  document.title = " Paal Dappa | Admin panel";
  return (
    <div className="page-content">
      <OrderStatusModal
        isOpen={isModalOpen}
        toggle={toggleModal}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        orderStatusLists={orderStatusLists?.map(status => ({ ...status, id: status.id.toString() }))}
        handleStatus={handleStatus}
        currentStatus={orderList?.find((order: any) => order.id === selectedOrderId)?.orderStatus?.id.toString()}
      />
      <OrderRouteUpdate
        isOpen={isRouteModalOpen}
        toggle={toggleRouteModal}
        selectedRoute={selectedRoute}
        setSelectedRoute={setSelectedRoute}
        ordersListRoute={ordersListRoute}
        handleRouteUpdate={handleRouteUpdate}
      />
      <OrderStoreUpdate
        isOpen={isStoreModalOpen}
        toggle={toggleStoreModal}
        selectedStore={selectedStore}
        setSelectedStore={setSelectedStore}
        ordersListStore={ordersListStore}
        handleStoreUpdate={handleStoreUpdate}
      />
      <OrderDeliveryDateUpdate
        isOpen={isDeliveryDateModalOpen}
        toggle={toggleDeliveryDateModal}
        selectedDeliveryDate={selectedDeliveryDate ? selectedDeliveryDate.toISOString() : ""}
        setSelectedDeliveryDate={(dateString: string) => setSelectedDeliveryDate(dateString ? new Date(dateString) : null)}
        handleDeliveryDateUpdate={handleDeliveryDateUpdate}
      />

      {/* Add the BulkStatusModal component */}
      <BulkStatusModal
        isOpen={bulkStatusModal}
        toggle={() => setBulkStatusModal(false)}
        selectedStatus={selectedBulkStatus}
        setSelectedStatus={setSelectedBulkStatus}
        orderStatusLists={orderStatusLists}
        handleBulkStatusUpdate={() => handleBulkStatusUpdate()}
      />

      {/* Add the BulkDriverModal component */}
      <BulkDriverModal
        isOpen={bulkDriverModal}
        toggle={() => setBulkDriverModal(false)}
        selectedDriver={selectedBulkDriver}
        setSelectedDriver={setSelectedBulkDriver}
        driversList={driversList}
        handleBulkDriverAssign={handleBulkDriverAssign}
        selectedOrdersCount={filteredOrdersForDriver?.length}
      />

      <ExportCSVModal
        show={isExportCSV}
        onCloseClick={() => setIsExportCSV(false)}
        data={orderList}
      />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteOrder}
        onCloseClick={() => setDeleteModal(false)}
      />
      <DeleteModal
        show={deleteModalMulti}
        onDeleteClick={() => setBulkStatusModal(true)} // Open bulk status modal instead of delete
        onCloseClick={() => setDeleteModalMulti(false)}
        content={`Are you sure you want to update status for ${selectedOrders.length} order(s)?`}
      />
      <Container fluid>
        <BreadCrumb title="Orders" pageTitle="Paal Dappa" />

        {/* Header Actions */}
        <Row className="mb-3 align-items-center">
          <Col xs={12} md={6}>
            <h4 className="fw-semibold mb-0">Order History ({orderList?.length})</h4>
          </Col>
          <Col xs={12} md={6}>
            <div className="d-flex flex-wrap justify-content-md-end gap-2 mt-2 mt-md-0">
              <button
                type="button"
                className="btn btn-info"
                onClick={downloadExcel}
              >
                <i className="ri-file-download-line align-bottom me-1" /> Download Excel
              </button>
            </div>
          </Col>
        </Row>

        {/* Bulk Actions Card - Only show when orders are selected */}
        {areAnyOrdersSelected && (
          <Card className="mb-3 bg-light">
            <CardBody className="py-2">
              <div className="d-flex align-items-center justify-content-between">
                <span className="fw-semibold text-danger fw-bold">
                  {selectedOrders.length} order(s) selected
                </span>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setBulkStatusModal(true)}
                  >
                    <i className="ri-edit-line me-1" /> Change Status
                  </button>

                  <button
                    className="btn btn-outline-warning btn-sm"
                    onClick={() => setBulkDriverModal(true)}
                    disabled={filteredOrdersForDriver.length === 0}
                  >
                    <i className="ri-user-shared-line me-1" /> Assign Driver ({filteredOrdersForDriver.length})
                  </button>

                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={clearAllSelections}
                  >
                    <i className="ri-close-line me-1" /> Clear Selection
                  </button>
                </div>
              </div>
              {filteredOrdersForDriver.length === 0 && areAnyOrdersSelected && (
                <div className="mt-2 text-warning small">
                  <i className="ri-information-line me-1" />
                  Note: To assign driver, selected orders must have routes assigned first.
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {/* Status Tabs */}
        <Card className="mb-4">
          <CardBody>
            <Nav className="nav-tabs nav-tabs-custom nav-success flex-wrap" role="tablist">
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "all" })}
                  onClick={() => toggleTab("all")}
                  href="#"
                >
                  <i className="ri-store-2-fill me-1" /> All Orders
                  <span className="badge bg-primary ms-1">{totalOrders}</span>
                </NavLink>
              </NavItem>

              {orderStatusLists?.map((status, index) => (
                <NavItem key={index}>
                  <NavLink
                    className={classnames({ active: activeTab === status.id.toString() })}
                    onClick={() => toggleTab(status.id.toString())}
                    href="#"
                  >
                    <i className="ri-truck-line me-1" /> {status.name}
                    <span className="badge bg-danger ms-1">{orderCounts[status.name] || 0}</span>
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          </CardBody>
        </Card>

        {/* Filter Section */}
        <Card className="mb-4">
          <CardBody>
            <Row className="gy-3">
              <Col md={5} lg={3}>
                <label className="form-label">Type</label>
                <div className="d-flex flex-wrap gap-2 pt-1">
                  <div className="form-check form-check-inline">
                    <input
                      type="radio"
                      className="form-check-input"
                      name="typeFilter"
                      id="type-all"
                      checked={selectedTypeFilter === 0}
                      onChange={() => setSelectedTypeFilter(0)}
                    />
                    <label className="form-check-label" htmlFor="type-all">
                      All
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      type="radio"
                      className="form-check-input"
                      name="typeFilter"
                      id="type-orders"
                      checked={selectedTypeFilter === 1}
                      onChange={() => setSelectedTypeFilter(1)}
                    />
                    <label className="form-check-label" htmlFor="type-orders">
                      Orders
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      type="radio"
                      className="form-check-input"
                      name="typeFilter"
                      id="type-subscription"
                      checked={selectedTypeFilter === 2}
                      onChange={() => setSelectedTypeFilter(2)}
                    />
                    <label className="form-check-label" htmlFor="type-subscription">
                      Subscription
                    </label>
                  </div>
                </div>
              </Col>

              <Col md={4} lg={2}>
                <label className="form-label">Start Date</label>
                <DatePicker
                  selected={startDate}
                  onChange={(date: Date | null) => setStartDate(date)}
                  className="form-control"
                  placeholderText="Select start date"
                />
              </Col>

              <Col md={4} lg={2}>
                <label className="form-label">End Date</label>
                <DatePicker
                  selected={endDate}
                  onChange={(date: Date | null) => setEndDate(date)}
                  className="form-control"
                  placeholderText="Select end date"
                />
              </Col>

              <Col md={4} lg={2}>
                <label className="form-label">Order Status</label>
                <select
                  className="form-control"
                  value={selectedStatusFilter}
                  onChange={(e) => setSelectedStatusFilter(e.target.value)}
                >
                  <option value="">All</option>
                  {orderStatusLists?.map((status, index) => (
                    <option key={index} value={status.name}>
                      {status.name}
                    </option>
                  ))}
                </select>
              </Col>

              <Col md={4} lg={2}>
                <label className="form-label">Route</label>
                <select
                  className="form-control"
                  value={selectedRouteFilter}
                  onChange={(e) => setSelectedRouteFilter(e.target.value)}
                >
                  <option value="">All</option>
                  {ordersListRoute?.map((route: any, index: any) => (
                    <option key={index} value={route.name}>
                      {route.name}
                    </option>
                  ))}
                </select>
              </Col>

              <Col md={4} lg={2}>
                <label className="form-label">User</label>
                <select
                  className="form-control"
                  value={selectedUserFilter}
                  onChange={(e) => setSelectedUserFilter(e.target.value)}
                >
                  <option value="">All</option>
                  {userList?.map((user: any, index: any) => (
                    <option key={index} value={user}>
                      {user}
                    </option>
                  ))}
                </select>
              </Col>

              {/* Driver Filter */}
              <Col md={4} lg={2}>
                <label className="form-label">Driver</label>
                <select
                  className="form-control"
                  value={selectedDriverFilter}
                  onChange={(e) => setSelectedDriverFilter(e.target.value)}
                >
                  <option value="">All</option>
                  {driversList?.map((driver: any, index: any) => (
                    <option key={index} value={driver.id}>
                      {driver.name}
                    </option>
                  ))}
                </select>
              </Col>
            </Row>

            <div className="mt-4 d-flex flex-wrap gap-2 justify-content-end">
              <button className="btn btn-primary" onClick={applyFilters}>
                Apply Filters
              </button>
              <button className="btn btn-outline-secondary" onClick={clearFilters}>
                Clear Filters
              </button>
            </div>
          </CardBody>
        </Card>

        {/* Table Section */}
        <Card>
          <CardBody>
            {orderList?.length !== 0 ? (
              <TableContainer
                columns={columns}
                data={orderList || []}
                isGlobalFilter={true}
                customPageSize={10}
                divClass="table-responsive table-card"
                tableClass="table table-bordered align-top table-nowrap"
                theadClass="table-light fw-bold"
                SearchPlaceholder="Search orders..."
              />
            ) : isLoading ? (
              <div className="text-center p-4">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="text-center text-danger fw-bold p-4">
                <h5>No Results Found</h5>
              </div>
            )}

            <ToastContainer closeButton={false} limit={1} />
          </CardBody>
        </Card>

      </Container>
    </div >
  );
};

export default Orders;